import React, { useMemo } from "react";
import { Compass } from "lucide-react";
import { ContentGrid } from "@/components/content/ContentGrid";
import { ContentItem } from "@/types/content";
import { getContentTypeFromUrl } from "@/utils/getContentTypeFromUrl";
import { useImportExploreContent } from "@/hooks/useImportExploreContent";

type ExploreCategory = {
  id: string;
  name: string;
  slug: string | null;
  accentColor: string | null;
};

type ExploreItem = {
  id: string;
  title: string;
  description: string | null;
  author: string | null;
  coverUrl: string | null;
  accentColor: string | null;
  categoryId: string | null;
  url: string | null;
  isFeatured: boolean;
};

interface ExplorePageProps {
  categories: ExploreCategory[];
  items: ExploreItem[];
  isLoading?: boolean;
}

export const ExplorePage: React.FC<ExplorePageProps> = ({
  categories,
  items,
  isLoading = false,
}) => {
  const hasItems = items && items.length > 0;
  const { importContent, importing, importingId } = useImportExploreContent();

  // Map explore items to ContentItem format for ContentGrid
  const contentItems: ContentItem[] = useMemo(() => {
    return items.map(item => ({
      id: item.id,
      title: item.title,
      type: getContentTypeFromUrl(item.url),
      thumbnailUrl: item.coverUrl,
      sourceUrl: item.url,
      createdAt: new Date().toISOString(),
      visibility: "public" as const,
    }));
  }, [items]);

  // Build custom href for explore items
  const buildExploreHref = (contentItem: ContentItem) => {
    const exploreItem = items.find(i => i.id === contentItem.id);
    return exploreItem?.url ?? `/explore/${contentItem.id}`;
  };

  // Handle explore card click - import content to user's library
  const handleExploreCardClick = (contentItem: ContentItem) => {
    if (importing) return;
    const exploreItem = items.find(i => i.id === contentItem.id);
    if (exploreItem) {
      importContent({
        id: exploreItem.id,
        title: exploreItem.title,
        url: exploreItem.url,
        coverUrl: exploreItem.coverUrl,
      });
    }
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-72px)]">
      {/* Header */}
      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-semibold">Explore</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Discover curated learning content across various topics and categories.
        </p>
      </header>

      {/* Content Grid */}
      {!isLoading && hasItems && (
        <ContentGrid
          items={contentItems}
          isLoading={isLoading}
          skeletonCount={8}
          buildHref={buildExploreHref}
          onCardClick={handleExploreCardClick}
          loadingItemId={importingId}
        />
      )}

      {/* Loading */}
      {isLoading && (
        <ContentGrid
          items={[]}
          isLoading={true}
          skeletonCount={8}
          buildHref={buildExploreHref}
        />
      )}

      {/* Empty state */}
      {!isLoading && !hasItems && (
        <div className="flex items-center justify-center h-full py-16">
          <div className="text-center max-w-xl px-4">
            <h2 className="text-xl font-semibold mb-2">
              No content found in this category.
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Check back later for new learning materials or explore other
              categories.
            </p>

            <div className="flex flex-wrap justify-center gap-3 text-xs text-muted-foreground">
              <div className="inline-flex items-center gap-2 border px-3 py-1 rounded-full">
                <Compass className="w-3.5 h-3.5" />
                <span>Explore other categories</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
